/**
 * <s-mystery-offer> — floating "Mystery Discount" pill and two-step popup.
 *
 * Step 1 asks which experience the shopper is seeking; the answer is added to
 * the native customer form's contact[tags] (e.g. "vibe-sleep"). Step 2 is the
 * native Shopify newsletter form. The pill can be dismissed (remembered in
 * localStorage). After a successful post (customer_posted=true) the popup
 * reopens on the thank-you step when this form was the one submitted. That step
 * shows the code from data-code (WELCOME10), a Copy button, and applies the code
 * via Shopify.actions.updateCart({ discountCodes }) like the cart discount field,
 * plus a background GET of /discount/<code> so it also carries to checkout.
 */
const DISMISS_KEY = 'tb-mystery-dismissed';
const SUBMIT_KEY = 'tb-mystery-submitted';

class SMysteryOffer extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.dialog = this.querySelector('dialog');
    this.pill = this.querySelector('[data-mystery-pill]');
    this.form = this.querySelector('form');
    this.tags = this.querySelector('input[name="contact[tags]"]');
    this.baseTags = this.tags?.value || '';

    if (!this.#storage('get', DISMISS_KEY) && this.pill) this.pill.hidden = false;

    this.addEventListener('click', (event) => this.#onClick(event), opts);
    this.form?.addEventListener('submit', () => this.#storage('set', SUBMIT_KEY, '1', 'session'), opts);
    this.#restoreAfterPost();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #storage(action, key, value, kind = 'local') {
    try {
      const store = kind === 'session' ? window.sessionStorage : window.localStorage;
      if (action === 'get') return store.getItem(key);
      if (action === 'set') store.setItem(key, value);
      if (action === 'remove') store.removeItem(key);
    } catch {
      return null;
    }
    return null;
  }

  #step(name) {
    for (const step of this.querySelectorAll('[data-mystery-step]')) {
      step.hidden = step.dataset.mysteryStep !== name;
    }
    const focusTarget = this.querySelector(`[data-mystery-step="${name}"] [data-autofocus]`);
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  #dismiss() {
    this.#storage('set', DISMISS_KEY, '1');
    if (this.pill) this.pill.hidden = true;
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-mystery-open]')) {
      this.#step('quiz');
      if (!this.dialog.open) this.dialog.showModal();
      return;
    }
    const copy = target.closest('[data-mystery-copy]');
    if (copy instanceof HTMLElement) {
      this.#copyCode(copy);
      return;
    }
    if (target.closest('[data-mystery-dismiss]')) {
      this.#dismiss();
      return;
    }
    const vibe = target.closest('[data-vibe]');
    if (vibe instanceof HTMLElement) {
      if (this.tags) this.tags.value = [this.baseTags, `vibe-${vibe.dataset.vibe}`].filter(Boolean).join(',');
      this.#step('email');
      return;
    }
    if (target.closest('[data-mystery-no]')) {
      this.dialog.close();
      this.#dismiss();
    }
  }

  get #code() {
    return (this.dataset.code || '').trim();
  }

  async #copyCode(button) {
    const code = this.#code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      const field = document.createElement('textarea');
      field.value = code;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      field.remove();
    }
    const copyLabel = button.querySelector('[data-copy-label]');
    const copiedLabel = button.querySelector('[data-copied-label]');
    if (copyLabel && copiedLabel) {
      copyLabel.hidden = true;
      copiedLabel.hidden = false;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        copyLabel.hidden = false;
        copiedLabel.hidden = true;
      }, 2000);
    }
  }

  async #applyCode() {
    const code = this.#code;
    if (!code) return;
    // Signing up for the Mystery offer counts as accepting WELCOME10 (see s-welcome-offer.js guard).
    try { localStorage.setItem('tb-welcome10-choice', JSON.stringify({ status: 'accepted', source: 'mystery', at: Date.now() })); } catch { /* storage unavailable */ }
    fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`, { credentials: 'same-origin' }).catch(() => {});
    try {
      const result = await window.Shopify?.actions?.updateCart?.(
        { discountCodes: [code] },
        { event: { context: 'cart' } }
      );
      if (!result || result.userErrors?.length) return;
      const applied = this.querySelector('[data-mystery-applied]');
      if (applied) applied.hidden = false;
    } catch {
      /* The code is still shown and can be copied. */
    }
  }

  #restoreAfterPost() {
    if (!this.#storage('get', SUBMIT_KEY, null, 'session')) return;
    this.#storage('remove', SUBMIT_KEY, null, 'session');
    const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    this.#step(posted ? 'done' : 'email');
    if (!posted) {
      const error = this.querySelector('[data-mystery-error]');
      if (error) error.hidden = false;
    } else {
      this.#dismiss();
      this.#applyCode();
    }
    this.dialog.showModal();
  }
}

if (!customElements.get('s-mystery-offer')) {
  customElements.define('s-mystery-offer', SMysteryOffer);
}
